import React from 'react';

interface PhoneMockupProps {
  children: React.ReactNode;
  className?: string;
  scale?: 'sm' | 'md' | 'lg';
}

/**
 * iPhoneスタイルのモックアップフレーム
 * 内部にReactコンポーネントを表示可能
 */
export const PhoneMockup: React.FC<PhoneMockupProps> = ({
  children,
  className = '',
  scale = 'md'
}) => {
  const scaleClasses = {
    sm: 'w-48 h-96',
    md: 'w-64 h-[520px]',
    lg: 'w-80 h-[640px]'
  };

  return (
    <div className={`relative ${scaleClasses[scale]} ${className}`}>
      {/* Phone frame */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2.5rem] shadow-2xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-slate-900 rounded-b-xl z-20" />

        {/* Side buttons */}
        <div className="absolute -left-0.5 top-24 w-0.5 h-8 bg-slate-700 rounded-l" />
        <div className="absolute -left-0.5 top-36 w-0.5 h-12 bg-slate-700 rounded-l" />
        <div className="absolute -left-0.5 top-52 w-0.5 h-12 bg-slate-700 rounded-l" />
        <div className="absolute -right-0.5 top-32 w-0.5 h-16 bg-slate-700 rounded-r" />

        {/* Screen area */}
        <div className="absolute inset-2 top-2 bg-white rounded-[2rem] overflow-hidden">
          {/* Status bar */}
          <div className="h-10 bg-slate-50 flex items-center justify-between px-6 text-xs text-slate-600 border-b border-slate-100">
            <span className="font-medium">9:41</span>
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z"/>
              </svg>
              <div className="flex gap-0.5">
                <div className="w-1 h-2 bg-slate-600 rounded-sm" />
                <div className="w-1 h-2.5 bg-slate-600 rounded-sm" />
                <div className="w-1 h-3 bg-slate-600 rounded-sm" />
                <div className="w-1 h-3.5 bg-slate-600 rounded-sm" />
              </div>
              <div className="w-6 h-3 bg-slate-600 rounded-sm flex items-center justify-end pr-0.5">
                <div className="w-4 h-2 bg-green-400 rounded-sm" />
              </div>
            </div>
          </div>

          {/* Content area */}
          <div className="h-[calc(100%-2.5rem)] overflow-hidden bg-slate-50">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ブラウザスタイルのモックアップフレーム
 * ダッシュボード等のデスクトップUI表示用
 */
interface BrowserMockupProps {
  children: React.ReactNode;
  className?: string;
  url?: string;
}

export const BrowserMockup: React.FC<BrowserMockupProps> = ({
  children,
  className = '',
  url = 'https://genbasafeai.com/dashboard'
}) => {
  return (
    <div className={`bg-slate-200 rounded-xl shadow-2xl overflow-hidden ${className}`}>
      {/* Browser chrome */}
      <div className="h-10 bg-slate-100 flex items-center px-4 gap-3 border-b border-slate-200">
        {/* Traffic lights */}
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>

        {/* URL bar */}
        <div className="flex-1 h-6 bg-white rounded-md flex items-center px-3 text-xs text-slate-500">
          <svg className="w-3 h-3 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          {url}
        </div>
      </div>

      {/* Content area */}
      <div className="bg-slate-50 overflow-hidden">
        {children}
      </div>
    </div>
  );
};
